
import { api } from '@/services/api'


import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { studentService, StudentStats, CollegeCourse, StudentFilterInfo } from '@/services/studentService'


interface Student {
  id: string;
  rollNumber: string;
  firstName: string;
  lastName: string;
  email: string;
  courseGroup: {
    id: string;
    name: string;
    code: string;
  };
  college: {
    address: string;
    code: string;
    name: string;
  };
  enrollmentDate: string;
}

interface StudentState {
  students: Student[];
  currentStudent: Student | null;
  isLoading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  stats: {
    totalActive: number;
    newThisMonth: number;
    courses: CollegeCourse[];
    isLoadingStats: boolean;
    statsError: string | null;
  };
  filterInfo: {
    data: StudentFilterInfo | null;
    isLoading: boolean;
    error: string | null;
  };
}

const initialState: StudentState = {
  students: [],
  currentStudent: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  stats: {
    totalActive: 0,
    newThisMonth: 0,
    courses: [],
    isLoadingStats: false,
    statsError: null,
  },
  filterInfo: {
    data: null,
    isLoading: false,
    error: null,
  },
};

// Async thunks for student statistics
export const fetchStudentStats = createAsyncThunk(
  "student/fetchStudentStats",
  async (_, { rejectWithValue }) => {
    try {
      const [totalActive, newThisMonth, courses] = await Promise.all([
        studentService.getTotalActiveStudents(),
        studentService.getNewStudentsThisMonth(),
        studentService.getCollegeCourses(),
      ]);

      return {
        totalActive: totalActive.count,
        newThisMonth: newThisMonth.count,
        courses,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch student statistics"
      );
    }
  }
);

export const fetchStudents = createAsyncThunk(
  "student/fetchStudents",
  async (
    params: {
      page?: number;
      limit?: number;
      search?: string;
      courseGroupId?: string;
      batchYear?: string;
      enrollmentStatus?: string;
      enrollmentStartDate?: string;
      enrollmentEndDate?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get("/students", { params });
      return response.data; // Backend returns { success, message, data, meta }
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch students"
      );
    }
  }
);

export const fetchStudentFilterInfo = createAsyncThunk(
  "student/fetchStudentFilterInfo",
  async (_, { rejectWithValue }) => {
    try {
      const filterInfo = await studentService.getStudentFilterInfo();
      return filterInfo;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch student filter information"
      );
    }
  }
);

// Get student by ID
export const fetchStudentById = createAsyncThunk(
  "student/fetchStudentById",
  async (id: string, { rejectWithValue }) => {
    try {
      const student = await studentService.getStudentById(id);
      return student;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch student"
      );
    }
  }
);

// Update student
export const updateStudent = createAsyncThunk(
  "student/updateStudent",
  async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
    try {
      const updatedStudent = await studentService.updateStudent(id, data);
      return updatedStudent;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update student"
      );
    }
  }
);

// Delete student
export const deleteStudent = createAsyncThunk(
  "student/deleteStudent",
  async (id: string, { rejectWithValue }) => {
    try {
      await studentService.deleteStudent(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete student"
      );
    }
  }
);

// Create student
export const createStudent = createAsyncThunk(
  "student/createStudent",
  async (data: any, { rejectWithValue }) => {
    try {
      const newStudent = await studentService.createStudent(data);
      return newStudent;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create student"
      );
    }
  }
);

const studentSlice = createSlice({
  name: "student",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentStudent: (state, action) => {
      state.currentStudent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.students = action.payload.data;
        state.pagination = {
          page: action.payload.meta.page,
          limit: action.payload.meta.limit,
          total: action.payload.meta.total,
          totalPages: action.payload.meta.totalPages,
        };
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchStudentStats.pending, (state) => {
        state.stats.isLoadingStats = true;
        state.stats.statsError = null;
      })
      .addCase(fetchStudentStats.fulfilled, (state, action) => {
        state.stats.isLoadingStats = false;
        state.stats.totalActive = action.payload.totalActive;
        state.stats.newThisMonth = action.payload.newThisMonth;
        state.stats.courses = action.payload.courses;
      })
      .addCase(fetchStudentStats.rejected, (state, action) => {
        state.stats.isLoadingStats = false;
        state.stats.statsError = action.payload as string;
      })
      .addCase(fetchStudentFilterInfo.pending, (state) => {
        state.filterInfo.isLoading = true;
        state.filterInfo.error = null;
      })
      .addCase(fetchStudentFilterInfo.fulfilled, (state, action) => {
        state.filterInfo.isLoading = false;
        state.filterInfo.data = action.payload;
      })
      .addCase(fetchStudentFilterInfo.rejected, (state, action) => {
        state.filterInfo.isLoading = false;
        state.filterInfo.error = action.payload as string;
      })
      // Individual student actions
      .addCase(fetchStudentById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchStudentById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentStudent = action.payload;
      })
      .addCase(fetchStudentById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Update student
      .addCase(updateStudent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateStudent.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update the student in the list
        const index = state.students.findIndex(
          (s) => s.id === action.payload.id
        );
        if (index !== -1) {
          state.students[index] = action.payload;
        }
        state.currentStudent = action.payload;
      })
      .addCase(updateStudent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Delete student
      .addCase(deleteStudent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteStudent.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove the student from the list
        state.students = state.students.filter((s) => s.id !== action.payload);
        state.currentStudent = null;
      })
      .addCase(deleteStudent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create student
      .addCase(createStudent.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createStudent.fulfilled, (state, action) => {
        state.isLoading = false;
        // Add the new student to the beginning of the list
        state.students.unshift(action.payload);
        state.currentStudent = action.payload;
      })
      .addCase(createStudent.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentStudent } = studentSlice.actions;
export default studentSlice.reducer;
